import React, { createContext, useMemo, useEffect, useContext, useState, useCallback } from "react";
import { useRouter } from "next/router";
import dayjs from "dayjs";

import { useAuthContext } from "@/contexts/auth";
import { validateDate } from "@/shared/utils/subscriptions";
import {
  deleteSubscription,
  fetchContentByDateRequest,
  fetchThoughtsByDateRequest,
  getCalendarForMonth,
  getSubscriptions
} from "@/shared/api/subscriptionsApi";

const SubscriptionsContext = createContext({});

export const SubscriptionsProvider = ({ children }) => {
  const router = useRouter();
  const { isLogin } = useAuthContext();

  const date = useMemo(() => {
    let date;
    const initialDate = router.query.subscription?.[1];
    if (initialDate && dayjs(initialDate).isValid()) {
      date = validateDate(new Date(initialDate));
    } else {
      date = validateDate(new Date());
    }
    return date;
  }, [router.query.subscription]);

  const [content, setContent] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [thoughts, setThoughts] = useState({});
  const [calendar, setCalendar] = useState({});

  const fetchMonthData = useCallback((date) => {
    const dateObject = dayjs(date);
    const year = dateObject.year();
    const month = dateObject.month() + 1;
    if (!(year in calendar) || !(month in calendar[year])) {
      getCalendarForMonth(year, month).then((dates) => {
        const updatedCalendar = { ...calendar };

        if (!(year in updatedCalendar)) {
          updatedCalendar[year] = {};
        }
        updatedCalendar[year][month] = dates;
        setCalendar(updatedCalendar);
      });
    }
  }, [calendar]);

  const fetchThoughts = useCallback(() => {
    if (isLogin && date && !(date in thoughts)) {
      fetchThoughtsByDateRequest(date).then((response) => {
        setThoughts(response);
      });
    }
  }, [thoughts, date, isLogin]);

  const fetchContent = useCallback(() => {
    if (isLogin && date && !(date in content)) {
      fetchContentByDateRequest(date)
        .then((response) => {
          setContent(response || {});
        }).catch(() => {
          setContent({});
        });
    }
  }, [content, date, isLogin]);

  const fetchSubscriptions = useCallback(() => {
    if (isLogin) {
      getSubscriptions()
        .then((response) => {
          setSubscriptions(response);
        }).catch(() => {
          setSubscriptions([]);
        });
    }
  }, [isLogin]);

  const unsubscribe = useCallback(async (id) => {
    const status = await deleteSubscription(id);
    if (status === 204) {
      setContent({
        [date]: content[date].filter((item) => item.subscription !== id)
      });
    }
  }, [content, date]);


  const isSubscribedBook = useCallback((book) => {
    return !!subscriptions?.find((sub) => sub.book.book_id === book.book_id);
  }, [subscriptions]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  return (
    <SubscriptionsContext.Provider value={{
      date,
      content,
      thoughts,
      calendar,
      subscriptions,

      // methods
      isSubscribedBook,
      fetchThoughts,
      fetchContent,
      fetchSubscriptions,
      fetchMonthData,
      unsubscribe
    }}>
      {children}
    </SubscriptionsContext.Provider>
  );
};

export const useSubscriptionsContext = () => useContext(SubscriptionsContext);
